import React from 'react';
import { ColorRepresentation, Matrix4, Quaternion, Vector3 } from 'three';

import { Component, Member, PlatePair, PlateTypeProperties } from '@paragon/contract-library';

import { getPlateTypeString } from '../../../material-lists';
import MemberVisual from './MemberVisual';
import { PhysicalComponentLabel } from './PhysicalComponentLabel';
import PlatePairVisual from './PlatePairVisual';

interface Props {
  readonly component: Component;

  readonly matrix?: readonly number[];

  /**
   * Members are passed in separately from the component to allow control over which members are rendered, the addition
   *   of hypothetical members, or to allow replacement of some members.
   */
  readonly members: readonly Member[];

  /**
   * Plate pairs are passed in separately from the component for the same reason as members. Additionally, this property
   *   can receive `undefined` or be not set to render the component design with no plates at all.
   */
  readonly platePairs?: readonly PlatePair[];

  /**
   * Required if any `platePairs` are set, and must contain `PlateTypeProperties` for each type of plate to be rendered.
   */
  readonly plateTypeProperties?: { [getPlateTypeStringResult: string]: PlateTypeProperties | undefined };

  readonly showComponentLabel: boolean;
  readonly showPieceLabels?: boolean;
  readonly respectZPosition?: boolean;

  readonly memberStyle?: 'wood' | ColorRepresentation;
  readonly showMemberEdges?: boolean;
}

const transformationMatrix = new Matrix4();

const position = new Vector3();
const rotation = new Quaternion();
const scale = new Vector3();

export default function PhysicalComponentVisual({
  component,
  matrix,
  members,
  platePairs,
  plateTypeProperties,
  showComponentLabel,
  showPieceLabels,
  respectZPosition,
  memberStyle,
  showMemberEdges,
}: Props) {
  transformationMatrix.fromArray(matrix || [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  transformationMatrix.decompose(position, rotation, scale);

  return (
    <group
      position={[position.x, position.y, respectZPosition ? position.z : 0]}
      quaternion={[rotation.x, rotation.y, rotation.z, rotation.w]}
      scale={[scale.x, scale.y, scale.z]}
    >
      {members.map((member) => (
        <MemberVisual
          key={`member-${member.guid}`}
          member={member}
          component={component}
          showLabel={showPieceLabels}
          showEdges={showMemberEdges}
          style={memberStyle ?? 'wood'}
        />
      ))}

      {platePairs &&
        platePairs.map((platePair) => {
          if (!plateTypeProperties) {
            throw new Error('Plate type properties must be provided if plate pairs are being rendered.');
          }

          const propertiesForThisPlateType =
            plateTypeProperties[getPlateTypeString(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType)];
          if (!propertiesForThisPlateType) throw new Error('Plate type properties could not be found');

          return (
            <PlatePairVisual
              key={`platePair-${platePair.guid}`}
              platePair={platePair}
              plateTypeProperties={propertiesForThisPlateType}
              showLabel={showPieceLabels}
            />
          );
        })}

      {showComponentLabel && <PhysicalComponentLabel component={component} />}
    </group>
  );
}
